import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { type CSSProperties, useState } from 'react'

import Reveal from '@/components/motion/Reveal'
import Stagger from '@/components/motion/Stagger'
import Button from '@/components/ui/Button'
import { useSetPersona } from '@/features/onboarding/api/mutations'
import { useOnboarding } from '@/features/onboarding/api/queries'
import { PERSONAS } from '@/features/onboarding/personas'
import type { PersonaId } from '@/features/onboarding/store'
import { requireAuth } from '@/lib/auth/guards'
import PersonaCard from '@/screens/onboarding/components/PersonaCard'
import StepProgress from '@/screens/onboarding/components/StepProgress'

/** The cards run their own stagger, started after the rows above them (back, steps, heading). */
const CARDS_AFTER_HEADING = { '--stagger-elapsed': '-200ms' } as CSSProperties

export const Route = createFileRoute('/onboarding/persona')({
  beforeLoad: requireAuth,
  component: WhosClosestScreen,
})

function WhosClosestScreen() {
  const navigate = useNavigate()
  const { data: onboarding, isPending: onboardingPending } = useOnboarding()
  const setPersona = useSetPersona()

  // The pick this visit, before it is saved. Kept apart from the query so
  // tapping a card rings it instantly instead of waiting on a round trip.
  const [pickedId, setPickedId] = useState<PersonaId | null>(null)

  // While the fetch is in flight `onboarding` is undefined — falling back to
  // the first card then would flash the wrong persona "selected" for a
  // returning member whose saved answer is, say, Sita, before correcting
  // itself a moment later. Only default once we actually know there is
  // nothing saved.
  const savedPersona = onboardingPending
    ? undefined
    : (PERSONAS.find((p) => p.id === onboarding?.persona) ?? PERSONAS[0])
  const active = pickedId ? PERSONAS.find((p) => p.id === pickedId) : savedPersona

  const goNext = () => {
    if (!active) return
    setPersona.mutate(active.id, {
      onSuccess: () => {
        void navigate({ to: '/onboarding/plan' })
      },
    })
  }

  return (
    <Reveal variant="fade-in" className="bg-background text-foreground min-h-dvh px-8 py-8">
      <Stagger className="relative mx-auto max-w-300">
        <Link
          to="/signup"
          aria-label="Back"
          // The glyph alone is 24px, under the 44px touch minimum. The hit
          // area is grown past the visual bounds with a transparent
          // `::before` (12px each side, 4-grid snapped from Figma's 10 —
          // R1, round up for a hit area) rather than by enlarging the icon,
          // matching IconButton's pattern for a control that must stay
          // visually small.
          // rest:ub — the grey goes white and the chevron slides 2px left over the fast step.
          className="text-muted-foreground hover:text-foreground focus-visible:text-foreground absolute top-0 left-0 transition-[color,translate] duration-fast before:absolute before:-inset-3 before:content-[''] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring-control motion-safe:hover:-translate-x-(--motion-nudge) lg:before:hidden"
        >
          <ChevronLeft className="size-6" aria-hidden />
        </Link>

        <StepProgress current={1} />

        <Link
          to="/onboarding/done"
          // rest:ub — Skip rests tertiary and goes white with an underline (offset 3).
          className="text-muted-foreground-subtle hover:text-foreground focus-visible:text-foreground absolute top-0 right-0 text-body-m underline-offset-3 transition-colors duration-fast hover:underline focus-visible:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring-control"
        >
          Skip
        </Link>

        <h1 className="mt-10 text-center text-display-l">Who represents you the best?</h1>

        {/* The cards are a list of their own, after the heading's rows. */}
        <Stagger
          as="fieldset"
          style={CARDS_AFTER_HEADING}
          className="mt-10 animate-none grid gap-6 md:grid-cols-2 xl:grid-cols-4"
        >
          <legend className="sr-only">Choose the person closest to you</legend>
          {PERSONAS.map((p) => (
            <PersonaCard
              key={p.id}
              persona={p}
              selected={active?.id === p.id}
              onSelect={setPickedId}
            />
          ))}
        </Stagger>

        {active && (
          <div className="mt-12 text-center">
            <p className="text-h-l">You’re {active.name} if…</p>
            <p className="text-muted-foreground mt-3 text-body-m">{active.youIf.join(' · ')}</p>
          </div>
        )}

        <div className="mt-10 flex justify-center gap-4">
          <Button
            size="hero"
            className="min-w-60"
            onClick={goNext}
            disabled={!active || setPersona.isPending}
          >
            {setPersona.isPending ? 'Saving…' : 'That’s me'}
          </Button>
          <Button
            variant="secondary"
            size="hero"
            className="min-w-40 text-h-s"
            onClick={() => {
              void navigate({ to: '/onboarding/plan' })
            }}
          >
            Not sure
          </Button>
        </div>

        {setPersona.isError && (
          <p role="alert" className="text-destructive mt-4 text-center text-body-s">
            Couldn’t save that. Check your connection and try again.
          </p>
        )}
      </Stagger>
    </Reveal>
  )
}
